import { CommonModule } from '@angular/common';
import {
  Component, computed, effect, ElementRef, input, model, viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { FieldState } from '../../../models/field.model';
import { InlineSvgComponent } from '../../inline-svg/inline-svg.component';

@Component({
  selector: 'ui-text-area-field',
  imports: [ CommonModule, FormsModule, InlineSvgComponent ],
  templateUrl: './text-area-field.component.html',
  styleUrl: './text-area-field.component.scss',
})
export class TextAreaFieldComponent {
  /** Defines the style of the text are field. */
  fieldState = FieldState;
  /** A unique name identifier for the text area component.
   *  The name is a string generated with a random hexadecimal number.
  */
  name = input<string>(`ui-text-area-field-${(Math.round((Math.random() * 100000000)).toString(16))}`);
  /** Output bind value. */
  value = model<string | undefined>('');
  /** Defines the current style of the text area field. */
  state = input<FieldState>(FieldState.Default);
  /** Defines the placeholder of the text area field. */
  placeholder = input<string>('');
  /** Alert text message to be displayed based on the value. */
  alert = input<string>();
  /** When enabled, the text area grows with its content instead of scrolling. */
  autoGrow = input<boolean>(false);
  /** Renders the field read-only, for example while the contract it belongs to is locked. */
  disabled = input<boolean>(false);

  /** Computed property that returns the icon
   * that should be displayed based on the FieldState.
  */
  icon = computed(() => {
    switch (this.state()) {
      case FieldState.Success:
      case FieldState.Info:
        return 'success';
      case FieldState.Invalid:
        return 'error';
      default:
        return '';
    }
  });

  private textarea = viewChild<ElementRef<HTMLTextAreaElement>>('textarea');

  constructor() {
    effect(() => {
      this.value();

      if (this.autoGrow()) {
        this.resize();
      }
    });

    effect((onCleanup) => {
      const element = this.textarea()?.nativeElement;

      if (!element || !this.autoGrow()) return;

      const observer = new ResizeObserver(() => this.resize());

      observer.observe(element);
      onCleanup(() => observer.disconnect());
    });
  }

  private resize(): void {
    const element = this.textarea()?.nativeElement;

    if (!element) return;

    element.style.height = 'auto';
    element.style.height = `${element.scrollHeight}px`;
  }
}
